import React from "react";
import { useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { trpc } from "~/lib/trpc";
import { cn } from "~/lib/utils";
import { useT, type DictKey } from "~/lib/i18n";
import { Combobox, useDebounced, type ComboboxOption } from "~/components/Combobox";

const COUNTRY_IDS = ["FR", "BE", "DE", "IT", "ES", "NL", "CH", "AT"] as const;

const CURRENT_YEAR = new Date().getFullYear();
const MIN_YEAR = 1985;
const YEAR_OPTIONS: number[] = Array.from(
  { length: CURRENT_YEAR - MIN_YEAR + 2 },
  (_, i) => CURRENT_YEAR + 1 - i,
);

const FUEL_OPTIONS = [
  { value: "diesel", label: "Diesel" },
  { value: "petrol", label: "Essence" },
  { value: "electric", label: "Électrique" },
  { value: "hybrid", label: "Hybride" },
  { value: "lpg", label: "GPL" },
] as const;

const GEARBOX_OPTIONS = [
  { value: "automatic", label: "Automatique" },
  { value: "manual", label: "Manuelle" },
] as const;

const BODY_OPTIONS = [
  { value: "sedan", label: "Berline" },
  { value: "suv", label: "SUV" },
  { value: "coupe", label: "Coupé" },
  { value: "convertible", label: "Cabriolet" },
  { value: "hatchback", label: "Compacte" },
  { value: "wagon", label: "Break" },
  { value: "van", label: "Monospace" },
  { value: "pickup", label: "Pick-up" },
] as const;

const DRIVE_OPTIONS = [
  { value: "awd", label: "4x4 / AWD" },
  { value: "fwd", label: "Traction" },
  { value: "rwd", label: "Propulsion" },
] as const;

export default function ScanNew(): React.JSX.Element {
  const { t } = useT();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlBrandSlug = searchParams.get("brand") ?? "";
  const urlModelSlug = searchParams.get("model") ?? "";

  const [brand, setBrand] = useState<ComboboxOption | null>(null);
  const [brandQuery, setBrandQuery] = useState("");
  const [model, setModel] = useState<ComboboxOption | null>(null);
  const [modelQuery, setModelQuery] = useState("");
  const [version, setVersion] = useState<ComboboxOption | null>(null);
  const [versionQuery, setVersionQuery] = useState("");
  const [textQuery, setTextQuery] = useState("");
  const [yearMin, setYearMin] = useState<number | "">("");
  const [yearMax, setYearMax] = useState<number | "">("");
  const [priceMin, setPriceMin] = useState<number | "">("");
  const [priceMax, setPriceMax] = useState<number | "">("");
  const [kmMax, setKmMax] = useState<number | "">("");
  const [countries, setCountries] = useState<string[]>(["FR", "BE", "DE"]);
  const [fuel, setFuel] = useState<string>("");
  const [gearbox, setGearbox] = useState<string>("");
  const [bodyType, setBodyType] = useState<string>("");
  const [driveType, setDriveType] = useState<string>("");
  const [hpMin, setHpMin] = useState<number | "">("");
  const [hpMax, setHpMax] = useState<number | "">("");
  const [showAdvanced, setShowAdvanced] = useState(false);

  const debouncedBrandQ = useDebounced(brandQuery, 180);
  const debouncedModelQ = useDebounced(modelQuery, 180);

  const brandsQ = trpc.taxo.searchBrands.useQuery(
    { q: debouncedBrandQ, limit: 20 },
    { staleTime: 60_000, placeholderData: (prev) => prev },
  );

  const modelsQ = trpc.taxo.searchModels.useQuery(
    { brandId: brand?.id ?? "", q: debouncedModelQ, limit: 40 },
    { staleTime: 60_000, enabled: !!brand, placeholderData: (prev) => prev },
  );

  const versionsQ = trpc.taxo.versionsByModel.useQuery(
    { modelId: model?.id ?? "" },
    { staleTime: 60_000, enabled: !!model },
  );

  const yearRangeQ = trpc.taxo.modelYearRange.useQuery(
    { brandSlug: brand?.slug ?? "", modelSlug: model?.slug ?? "" },
    { enabled: !!brand && !!model, staleTime: 120_000 },
  );

  const slugPrefillQ = trpc.fairPrice.topDealsBySlug.useQuery(
    { brandSlug: urlBrandSlug, modelSlug: urlModelSlug, limit: 1 },
    { enabled: urlBrandSlug.length > 0, staleTime: 60_000 },
  );

  React.useEffect(() => {
    const data = slugPrefillQ.data;
    if (!data?.brand || brand) return;
    setBrand({
      id: data.brand.id,
      slug: urlBrandSlug,
      name: data.brand.name,
    });
    if (data.model && !model) {
      setModel({
        id: data.model.id,
        slug: urlModelSlug,
        name: data.model.name,
      });
    }
  }, [slugPrefillQ.data, urlBrandSlug, urlModelSlug, brand, model]);

  const yearWarning = (() => {
    const range = yearRangeQ.data;
    if (!range) return null;
    const selectedYear = yearMin !== "" ? Number(yearMin) : null;
    if (!selectedYear) return null;
    if (range.yearMin && selectedYear < range.yearMin) {
      return `Ce modèle n'a des annonces qu'à partir de ${range.yearMin}. Les résultats pour ${selectedYear} seront probablement vides.`;
    }
    if (range.yearMax && selectedYear > range.yearMax) {
      return `Les annonces de ce modèle vont jusqu'à ${range.yearMax}. Les résultats pour ${selectedYear} seront probablement vides.`;
    }
    return null;
  })();

  const onBrandChange = useCallback((b: ComboboxOption | null) => {
    setBrand(b);
    setModel(null);
    setModelQuery("");
    setVersion(null);
    setVersionQuery("");
  }, []);

  const onModelChange = useCallback((m: ComboboxOption | null) => {
    setModel(m);
    setVersion(null);
    setVersionQuery("");
  }, []);

  const utils = trpc.useUtils();
  const createScan = trpc.scan.create.useMutation();

  const canSubmit = !!brand && !createScan.isPending;

  async function launchScan(): Promise<void> {
    if (!brand) return;
    try {
      const lowerCountries = countries.map((c) => c.toLowerCase());
      const filters: Record<string, unknown> = {
        brand: brand.slug,
      };
      if (model) filters.model = model.slug;
      if (version) filters.variant = version.slug;
      if (textQuery.trim()) filters.textQuery = textQuery.trim();
      if (yearMin !== "") filters.yearMin = Number(yearMin);
      if (yearMax !== "") filters.yearMax = Number(yearMax);
      if (priceMin !== "") filters.priceMinEur = Number(priceMin);
      if (priceMax !== "") filters.priceMaxEur = Number(priceMax);
      if (kmMax !== "") filters.kmMax = Number(kmMax);
      if (lowerCountries.length) filters.countries = lowerCountries;
      if (fuel) filters.fuel = fuel;
      if (gearbox) filters.gearbox = gearbox;
      if (bodyType) filters.bodyType = bodyType;
      if (driveType) filters.driveType = driveType;
      if (hpMin !== "") filters.hpMin = Number(hpMin);
      if (hpMax !== "") filters.hpMax = Number(hpMax);

      const result = await createScan.mutateAsync({
        filters: filters as any,
      });
      void utils.scan.get.prefetch({ id: result.id });
      navigate(`/scan/${result.id}`);
    } catch (err) {
      console.error("scan creation failed", err);
    }
  }

  return (
    <div className="w-full min-h-[calc(100dvh-60px)] flex flex-col">
      <div className="flex-1 w-full max-w-[680px] mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-display text-[22px] sm:text-[28px] leading-tight text-ink-primary">
            {t("web.scanNew.h1")}
          </h1>
          <p className="mt-2 text-[13px] sm:text-[14px] text-ink-secondary max-w-[55ch]">
            {t("web.scanNew.subtitleFull")}
          </p>
        </div>

        {/* Main form */}
        <div className="space-y-5">
          {/* Brand + Model */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={t("web.scanNew.brand.label")} required>
              <Combobox
                value={brand}
                onChange={onBrandChange}
                query={brandQuery}
                onQueryChange={setBrandQuery}
                options={(brandsQ.data ?? []) as ComboboxOption[]}
                loading={brandsQ.isFetching}
                placeholder={t("web.scanNew.brand.placeholder")}
                emptyHint={t("web.scanNew.brand.empty")}
              />
            </Field>
            <Field label={t("web.scanNew.model.label")}>
              <Combobox
                value={model}
                onChange={onModelChange}
                query={modelQuery}
                onQueryChange={setModelQuery}
                options={(modelsQ.data ?? []) as ComboboxOption[]}
                loading={modelsQ.isFetching}
                disabled={!brand}
                placeholder={
                  brand
                    ? t("web.scanNew.model.placeholder")
                    : t("web.scanNew.model.disabled")
                }
                emptyHint={t("web.scanNew.model.empty")}
              />
            </Field>
          </div>

          {/* Version + Keywords */}
          {model && (versionsQ.data?.length ?? 0) > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Version / Variante">
                <Combobox
                  value={version}
                  onChange={setVersion}
                  query={versionQuery}
                  onQueryChange={setVersionQuery}
                  options={(versionsQ.data ?? []).map((v: any) => ({ id: v.id, slug: v.slug, name: v.name })) as ComboboxOption[]}
                  loading={versionsQ.isFetching}
                  placeholder="Ex: GTS, Turbo S, GT3..."
                  emptyHint="Aucune version trouvée"
                />
              </Field>
              <Field label="Mots-clés (optionnel)">
                <input
                  type="text"
                  value={textQuery}
                  onChange={(e) => setTextQuery(e.target.value)}
                  placeholder="Ex: 991.2, PDK, Sport Chrono..."
                  className={inputCls}
                />
              </Field>
            </div>
          )}

          {/* Keywords standalone (when no version list available) */}
          {(!model || (versionsQ.data?.length ?? 0) === 0) && (
            <Field label="Mots-clés (préciser la recherche)">
              <input
                type="text"
                value={textQuery}
                onChange={(e) => setTextQuery(e.target.value)}
                placeholder="Ex: 991.2 GTS, RS6 Performance, Quadrifoglio..."
                className={inputCls}
              />
            </Field>
          )}

          {/* Year + Budget + Km */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label={t("web.scanNew.year.from")}>
              <YearSelect
                value={yearMin}
                onChange={(v) => {
                  setYearMin(v);
                  if (v !== "" && yearMax !== "" && Number(v) > Number(yearMax)) setYearMax(v);
                }}
                years={YEAR_OPTIONS}
              />
            </Field>
            <Field label={t("web.scanNew.year.to")}>
              <YearSelect
                value={yearMax}
                onChange={(v) => {
                  setYearMax(v);
                  if (v !== "" && yearMin !== "" && Number(v) < Number(yearMin)) setYearMin(v);
                }}
                years={YEAR_OPTIONS}
              />
            </Field>
            <Field label="Prix min (€)">
              <input
                type="number"
                inputMode="numeric"
                value={priceMin}
                onChange={(e) => setPriceMin(e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="5 000"
                className={inputCls}
              />
            </Field>
            <Field label="Prix max (€)">
              <input
                type="number"
                inputMode="numeric"
                value={priceMax}
                onChange={(e) => setPriceMax(e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="80 000"
                className={inputCls}
              />
            </Field>
          </div>

          {/* Year validation warning */}
          {yearWarning && (
            <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40">
              <svg className="h-4 w-4 mt-0.5 text-amber-600 dark:text-amber-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <p className="text-[12px] text-amber-800 dark:text-amber-300">{yearWarning}</p>
            </div>
          )}

          {/* Km + Fuel + Gearbox */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Field label="Km max">
              <input
                type="number"
                inputMode="numeric"
                value={kmMax}
                onChange={(e) => setKmMax(e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="100 000"
                className={inputCls}
              />
            </Field>
            <Field label="Carburant">
              <ChipSelect
                options={FUEL_OPTIONS}
                value={fuel}
                onChange={setFuel}
                placeholder="Tous"
              />
            </Field>
            <Field label="Boîte">
              <ChipSelect
                options={GEARBOX_OPTIONS}
                value={gearbox}
                onChange={setGearbox}
                placeholder="Toutes"
              />
            </Field>
          </div>

          {/* Countries */}
          <Field label={t("web.scanNew.countries.label")}>
            <div className="flex flex-wrap gap-1.5">
              {COUNTRY_IDS.map((id) => {
                const on = countries.includes(id);
                const labelKey = `country.full.${id.toLowerCase()}` as DictKey;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() =>
                      setCountries(on ? countries.filter((x) => x !== id) : [...countries, id])
                    }
                    className={cn(
                      "h-8 px-3 text-[12px] rounded-full transition-colors duration-150",
                      on
                        ? "bg-[var(--accent-soft)] text-[var(--accent)] font-medium"
                        : "bg-surface-card text-ink-secondary border border-[var(--divider-strong)] hover:bg-surface-hover",
                    )}
                  >
                    {t(labelKey)}
                  </button>
                );
              })}
            </div>
          </Field>

          {/* Advanced toggle */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-1.5 text-[12.5px] text-ink-secondary hover:text-ink-primary transition-colors"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              className={cn("transition-transform duration-200", showAdvanced && "rotate-90")}
            >
              <path d="M4 2l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.5" />
            </svg>
            Filtres avancés
          </button>

          {/* Advanced filters */}
          {showAdvanced && (
            <div className="space-y-4 pt-1 animate-in fade-in slide-in-from-top-1 duration-200">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Field label="Carrosserie">
                  <ChipSelect
                    options={BODY_OPTIONS}
                    value={bodyType}
                    onChange={setBodyType}
                    placeholder="Toutes"
                  />
                </Field>
                <Field label="Transmission">
                  <ChipSelect
                    options={DRIVE_OPTIONS}
                    value={driveType}
                    onChange={setDriveType}
                    placeholder="Toutes"
                  />
                </Field>
                <Field label="Puissance (ch)">
                  <div className="flex gap-2">
                    <input
                      type="number"
                      inputMode="numeric"
                      value={hpMin}
                      onChange={(e) => setHpMin(e.target.value === "" ? "" : Number(e.target.value))}
                      placeholder="Min"
                      className={cn(inputCls, "w-1/2")}
                    />
                    <input
                      type="number"
                      inputMode="numeric"
                      value={hpMax}
                      onChange={(e) => setHpMax(e.target.value === "" ? "" : Number(e.target.value))}
                      placeholder="Max"
                      className={cn(inputCls, "w-1/2")}
                    />
                  </div>
                </Field>
              </div>
            </div>
          )}
        </div>

        {/* Submit area */}
        <div className="mt-8 sm:mt-10 pt-5 border-t border-[var(--divider-weak)]">
          <button
            disabled={!canSubmit}
            onClick={launchScan}
            className={cn(
              "w-full sm:w-auto inline-flex h-11 items-center justify-center gap-2 rounded-lg",
              "bg-[var(--accent)] px-6 text-[14px] font-medium text-[var(--ink-on-accent)]",
              "disabled:opacity-40 disabled:cursor-not-allowed",
              "hover:brightness-110 active:scale-[0.98] transition-all duration-150",
            )}
          >
            {createScan.isPending ? (
              <span className="inline-block w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
            ) : null}
            {t("web.scanNew.submit")}
          </button>
          {!brand && (
            <p className="mt-2 text-[11.5px] text-ink-tertiary">
              Sélectionnez au minimum une marque pour lancer le scan.
            </p>
          )}
          {createScan.isError && (
            <p className="mt-2 text-[12px] text-red-500">
              {createScan.error?.message ?? "Erreur lors de la création"}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function YearSelect({
  value,
  onChange,
  years,
}: {
  value: number | "";
  onChange: (v: number | "") => void;
  years: number[];
}): React.JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
      className={cn(inputCls, "cursor-pointer appearance-none bg-no-repeat")}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='none' stroke='%23999' stroke-width='1.4' d='M1 1l4 4 4-4'/></svg>\")",
        backgroundPosition: "right 10px center",
      }}
    >
      <option value="">—</option>
      {years.map((y) => (
        <option key={y} value={y}>{y}</option>
      ))}
    </select>
  );
}

function ChipSelect({
  options,
  value,
  onChange,
  placeholder,
}: {
  options: readonly { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}): React.JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(inputCls, "cursor-pointer appearance-none bg-no-repeat")}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='none' stroke='%23999' stroke-width='1.4' d='M1 1l4 4 4-4'/></svg>\")",
        backgroundPosition: "right 10px center",
      }}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <label className="block">
      <span className="block text-[12px] font-medium text-ink-primary tracking-[0.01em] mb-1.5">
        {label}
        {required && <span className="text-[var(--accent)] ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}

const inputCls = cn(
  "w-full h-10 px-3 text-[13.5px] rounded-lg",
  "bg-surface-card border border-[var(--divider-strong)] text-ink-primary",
  "placeholder:text-ink-tertiary",
  "focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/20 focus:border-[var(--accent)]",
  "transition-all duration-150",
);
